import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { Bell, LogOut, Wifi, WifiOff, Menu, X, Plus } from "lucide-react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { useIdleTimer } from "../lib/useIdleTimer";
import IdleWarningModal from "./IdleWarningModal";
import ReconnectingOverlay from "./ReconnectingOverlay";
import DeadlineAlertCenter from "./DeadlineAlertCenter";
import CommandPalette from "./CommandPalette";
import FocusMode from "./FocusMode";

const NAV = [
  { to: "/app/dashboard", label: "Dashboard", icon: "dashboard", testid: "nav-dashboard" },
  { to: "/app/messages", label: "Chats", icon: "forum", testid: "nav-messages" },
  { to: "/app/stream-history", label: "Stream History", icon: "history", testid: "nav-stream-history" },
  { to: "/app/tasks", label: "Tasks", icon: "assignment", testid: "nav-tasks" },
  { to: "/app/calendar", label: "Calendar", icon: "calendar_month", testid: "nav-calendar" },
  { to: "/app/events", label: "Events", icon: "celebration", testid: "nav-events" },
  { to: "/app/review", label: "Review Queue", icon: "fact_check", testid: "nav-review" },
  { to: "/app/leads", label: "Leads", icon: "person_add", testid: "nav-leads" },
  { to: "/app/whatsapp", label: "WhatsApp Connect", icon: "chat", testid: "nav-whatsapp" },
  { to: "/app/settings", label: "Settings", icon: "settings", testid: "nav-settings" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [waConnected, setWaConnected] = useState(false);
  const [notifCount, setNotifCount] = useState(0);
  const [showNotif, setShowNotif] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showIdleWarning, setShowIdleWarning] = useState(false);
  const [leadsEnabled, setLeadsEnabled] = useState(false);

  const refreshStatus = async () => {
    try {
      const [s, n, ls] = await Promise.all([
        api.get("/whatsapp/status"),
        api.get("/notifications"),
        api.get("/leads/settings").catch(() => ({ data: { enabled: false } })),
      ]);
      setWaConnected(s.data.connected);
      setNotifCount(n.data.count || 0);
      setNotifs(n.data.notifications || []);
      setLeadsEnabled(!!ls.data?.enabled);
    } catch {}
  };

  useEffect(() => {
    refreshStatus();
    const t = setInterval(refreshStatus, 20000);
    return () => clearInterval(t);
  }, []);

  const handleIdleWarn = useCallback(() => setShowIdleWarning(true), []);
  const handleIdleExpire = useCallback(() => { setShowIdleWarning(false); logout(); }, [logout]);

  // Inactivity logout is opt-in (Settings → Security); 0 / unset = never.
  const idleMinutes = Number(user?.idleLogoutMinutes) || 0;
  const { reset: resetIdleTimer } = useIdleTimer({
    warnAfterMs: Math.max(idleMinutes * 60 - 60, 30) * 1000,   // the warning counts down the last 60 s
    expireAfterMs: Math.max(idleMinutes, 1) * 60 * 1000,
    onWarn: handleIdleWarn,
    onExpire: handleIdleExpire,
    enabled: idleMinutes > 0,
  });

  const initials = user?.name
    ? user.name.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2)
    : "U";

  return (
    <div className="min-h-screen flex bg-[#faf8ff]" data-testid="app-shell">
      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 h-full w-64 bg-[#0F172A] flex flex-col py-6 border-r border-slate-800 z-50 transition-transform duration-300 ${
          mobileMenuOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="px-6 mb-8">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-blue-600 rounded-lg flex items-center justify-center shrink-0">
              <span className="material-symbols-outlined text-white text-[18px]" style={{ fontVariationSettings: "'FILL' 1" }}>bolt</span>
            </div>
            <div>
              <h1 className="text-[17px] font-extrabold text-white leading-none tracking-tight">Brainydo</h1>
              <p className="text-[9px] text-slate-400 uppercase tracking-[0.2em] font-semibold mt-1">WhatsApp Intelligence</p>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 space-y-0.5 overflow-y-auto px-0">
          {NAV.filter((n) => n.to !== "/app/leads" || leadsEnabled).map(({ to, label, icon, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              onClick={() => setMobileMenuOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 py-3 px-4 border-l-4 transition-all duration-200 text-[13.5px] font-medium ${
                  isActive
                    ? "bg-blue-600/10 text-white border-blue-500"
                    : "text-slate-400 hover:text-slate-200 hover:bg-slate-800/50 border-transparent"
                }`
              }
            >
              <span className="material-symbols-outlined text-[20px]">{icon}</span>
              <span className="tracking-wide">{label}</span>
            </NavLink>
          ))}
        </nav>

        {/* Bottom: New Task + WA status + Sign Out */}
        <div className="mt-auto px-4 space-y-3">
          <button
            onClick={() => { nav("/app/tasks"); setMobileMenuOpen(false); }}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 px-4 rounded-xl flex items-center justify-center gap-2 transition-all active:scale-95 text-[13px]"
          >
            <Plus size={16} />
            New Task
          </button>

          <div className="pt-3 border-t border-slate-800 space-y-0.5">
            <div
              className="flex items-center gap-3 py-2 px-2 text-[12px]"
              data-testid="wa-status"
            >
              {waConnected ? (
                <>
                  <Wifi size={14} className="text-emerald-400" />
                  <span className="text-emerald-400 font-medium">WhatsApp Connected</span>
                </>
              ) : (
                <>
                  <WifiOff size={14} className="text-slate-500" />
                  <span className="text-slate-500">Not Connected</span>
                </>
              )}
            </div>
            <button
              onClick={logout}
              className="w-full flex items-center gap-3 py-2 px-2 text-slate-400 hover:text-slate-200 transition-colors text-[13px]"
              data-testid="logout-btn"
            >
              <LogOut size={16} />
              Sign Out
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 lg:hidden z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 lg:ml-64">
        {/* Topbar */}
        <header className="h-16 bg-white border-b border-slate-200 px-4 lg:px-6 flex items-center justify-between gap-3 sticky top-0 z-40">
          <div className="flex items-center gap-4 flex-1">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden w-9 h-9 flex items-center justify-center rounded-lg hover:bg-slate-100 transition"
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
            <div className="relative hidden md:block flex-1 max-w-md">
              <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[18px]">search</span>
              <button
                type="button"
                onClick={() => window.dispatchEvent(new CustomEvent("brainydo:command-palette"))}
                className="w-full text-left bg-slate-50 border border-slate-200 hover:border-slate-300 hover:bg-white rounded-xl pl-10 pr-3 py-2 text-[13px] text-slate-400 transition-all flex items-center"
                data-testid="topbar-search"
              >
                Search tasks, chats, people…
                <kbd className="ml-auto text-[10.5px] font-mono text-slate-400 bg-white border border-slate-200 rounded px-1.5">⌘K</kbd>
              </button>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden lg:block text-right">
              <p className="text-[12px] font-semibold text-slate-800">Workspace</p>
              <p className="text-[10px] text-slate-500 uppercase tracking-wider">{user?.name || "User"}</p>
            </div>

            {/* Notifications */}
            <div className="relative">
              <button
                onClick={() => setShowNotif((v) => !v)}
                className="relative w-9 h-9 grid place-items-center rounded-lg hover:bg-slate-100 transition"
                data-testid="notifications-btn"
              >
                <Bell size={18} className="text-slate-600" />
                {notifCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold grid place-items-center">
                    {notifCount}
                  </span>
                )}
              </button>
              {showNotif && (
                <div
                  className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] bg-white border border-slate-200 rounded-xl shadow-lg z-50"
                  data-testid="notifications-panel"
                >
                  <div className="px-4 py-3 border-b border-slate-100 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                    Notifications
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifs.length === 0 && (
                      <div className="px-4 py-6 text-center text-[13px] text-slate-500">
                        You're all caught up.
                      </div>
                    )}
                    {notifs.map((n) => (
                      <div
                        key={n.id}
                        className="px-4 py-3 border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                        onClick={() => {
                          setShowNotif(false);
                          if (n.task_id) nav(`/app/tasks?focus=${n.task_id}`);
                          else if (n.type === "review") nav("/app/review");
                        }}
                        data-testid={`notif-${n.type}`}
                      >
                        <div className="flex items-center gap-2">
                          <span className={
                            "w-2 h-2 rounded-full " +
                            (n.type === "urgent" ? "bg-red-600" :
                             n.type === "overdue" ? "bg-amber-600" : "bg-blue-600")
                          } />
                          <span className="text-[12.5px] font-semibold text-slate-900">{n.title}</span>
                        </div>
                        <div className="text-[12px] text-slate-600 mt-1 line-clamp-2">{n.message}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Avatar */}
            <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-[11px] font-bold border border-blue-200">
              {initials}
            </div>
          </div>
        </header>

        {/* Subscription expiry warning banner */}
        {(() => {
          const sub = user?.subscription;
          if (!sub) return null;
          const { status, days_remaining } = sub;
          const showBanner =
            status === "trial_expired" || status === "expired" || status === "suspended" ||
            (status === "trial" && days_remaining !== 9999 && days_remaining <= 5);
          if (!showBanner) return null;
          const isSuspended = status === "suspended";
          const isExpired = status === "trial_expired" || status === "expired";
          return (
            <div className={`px-4 py-2.5 flex items-center gap-3 text-[12.5px] font-medium ${
              isSuspended ? "bg-slate-800 text-white" :
              isExpired ? "bg-red-600 text-white" :
              "bg-amber-500 text-white"
            }`}>
              <span className="material-symbols-outlined text-[16px] shrink-0" style={{ fontVariationSettings: "'FILL' 1" }}>
                {isSuspended ? "block" : isExpired ? "error" : "warning"}
              </span>
              <span>
                {isSuspended ? "Your account is suspended." :
                 isExpired ? "Your subscription has expired." :
                 `Your trial expires in ${days_remaining} day${days_remaining !== 1 ? "s" : ""}.`}
              </span>
              <a
                href="/app/settings"
                className="ml-auto shrink-0 underline opacity-80 hover:opacity-100 transition"
              >
                View plan details
              </a>
            </div>
          );
        })()}

        <main className="flex-1 overflow-y-auto">
          <Outlet context={{ refreshStatus }} />
        </main>
      </div>

      {/* Deadline alerts — top-right stack, persistent across all pages */}
      <DeadlineAlertCenter />

      {/* ⌘K search and full-screen focus mode, reachable from any page */}
      <CommandPalette />
      <FocusMode />

      {showIdleWarning && (
        <IdleWarningModal
          onStay={() => { setShowIdleWarning(false); resetIdleTimer(); }}
          onLogout={() => { setShowIdleWarning(false); logout(); }}
        />
      )}

      {/* "Welcome back, reconnecting…" overlay — shows only on login when the
          user has a saved WA session that's reconnecting in the background. */}
      <ReconnectingOverlay />
    </div>
  );
}
